import { useState } from "react";
import { toast } from "sonner";
import { Download, Link2, Loader2, Share2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BACKEND_URL } from "@/lib/apiBase";

const ICONS = {
  x: "M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z",
  facebook: "M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.413c0-3.025 1.792-4.697 4.533-4.697 1.312 0 2.686.236 2.686.236v2.971h-1.513c-1.491 0-1.956.93-1.956 1.886v2.264h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z",
  whatsapp: "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347M12.05 21.785h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z",
  pinterest: "M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z",
};

const slug = (s) => (s || "wine").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

function SocialLink({ href, icon, label, testId }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Share on ${label}`}
      data-testid={testId}
      className="flex flex-col items-center gap-1.5 group"
    >
      <span className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-white/70 group-hover:text-gold group-hover:border-gold/50 transition-colors duration-200">
        <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="currentColor"><path d={ICONS[icon]} /></svg>
      </span>
      <span className="font-body text-[10px] text-white/40">{label}</span>
    </a>
  );
}

export default function ShareWineDialog({ wine }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const cardUrl = `${BACKEND_URL}/api/wines/${wine.id}/share-card`;
  const shareUrl = `${BACKEND_URL}/api/share/wines/${wine.id}`;
  const text = `${wine.name} ${wine.year} scored ${wine.rating} on AlfredWine`;
  const fileName = `alfredwine-${slug(wine.name)}-${wine.year}.png`;
  const enc = encodeURIComponent;

  const getFile = async () => {
    const res = await fetch(cardUrl);
    if (!res.ok) throw new Error("card fetch failed");
    return new File([await res.blob()], fileName, { type: "image/png" });
  };

  const download = async () => {
    setBusy("download");
    try {
      const url = URL.createObjectURL(await getFile());
      const a = Object.assign(document.createElement("a"), { href: url, download: fileName });
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      toast.error("Couldn't download the image. Please try again.");
    } finally {
      setBusy("");
    }
  };

  const nativeShare = async () => {
    setBusy("native");
    try {
      const file = await getFile();
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: wine.name, text: `${text} ${shareUrl}` });
      } else {
        await navigator.share({ title: wine.name, text, url: shareUrl });
      }
    } catch (err) {
      if (err?.name !== "AbortError") toast.error("Sharing isn't available on this device.");
    } finally {
      setBusy("");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy the link.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="share-wine-btn" variant="outline" size="sm" className="border-white/15 text-white/80 hover:bg-white/5 hover:text-white h-9 gap-2 font-body text-xs">
          <Share2 className="w-4 h-4" /> Share
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-wine-paper border-white/10 max-w-md max-h-[92vh] overflow-y-auto" data-testid="share-wine-dialog">
        <DialogHeader>
          <DialogTitle className="text-white/90 font-display text-2xl">Share this wine</DialogTitle>
          <DialogDescription className="text-white/50 font-body text-sm">A branded 1080×1080 card, ready for Instagram, Facebook, WhatsApp and X.</DialogDescription>
        </DialogHeader>
        {open && (
          <img src={cardUrl} alt={`${wine.name} share card`} data-testid="share-card-preview" className="w-full aspect-square rounded-sm border border-white/10 bg-black/40" />
        )}
        <div className="grid grid-cols-2 gap-2">
          <Button onClick={download} disabled={!!busy} data-testid="share-download-btn" className="bg-merlot hover:bg-merlot-light text-white gap-2 h-10 font-body text-sm">
            {busy === "download" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download
          </Button>
          {typeof navigator !== "undefined" && navigator.share ? (
            <Button onClick={nativeShare} disabled={!!busy} variant="outline" data-testid="share-native-btn" className="border-gold/40 text-gold hover:bg-gold/10 gap-2 h-10 font-body text-sm">
              {busy === "native" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share…
            </Button>
          ) : (
            <Button onClick={copyLink} variant="outline" data-testid="share-copy-link-btn" className="border-gold/40 text-gold hover:bg-gold/10 gap-2 h-10 font-body text-sm">
              <Link2 className="w-4 h-4" /> Copy link
            </Button>
          )}
        </div>
        <div className="flex items-start justify-between px-2 pt-1">
          <SocialLink icon="x" label="X" testId="share-x" href={`https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(shareUrl)}`} />
          <SocialLink icon="facebook" label="Facebook" testId="share-facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}`} />
          <SocialLink icon="whatsapp" label="WhatsApp" testId="share-whatsapp" href={`https://wa.me/?text=${enc(`${text} ${shareUrl}`)}`} />
          <SocialLink icon="pinterest" label="Pinterest" testId="share-pinterest" href={`https://pinterest.com/pin/create/button/?url=${enc(shareUrl)}&media=${enc(cardUrl)}&description=${enc(text)}`} />
          {typeof navigator !== "undefined" && navigator.share && (
            <button type="button" onClick={copyLink} data-testid="share-copy-link-btn" className="flex flex-col items-center gap-1.5 group">
              <span className="w-11 h-11 rounded-full border border-white/10 flex items-center justify-center text-white/70 group-hover:text-gold group-hover:border-gold/50 transition-colors duration-200">
                <Link2 className="w-[18px] h-[18px]" />
              </span>
              <span className="font-body text-[10px] text-white/40">Copy link</span>
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
